import { useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Popover,
  Stack,
  Typography,
} from '@mui/material'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import dayjs from 'dayjs'
import DateRangeCalendarPicker from '../DateRangeCalendarPicker'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

// Shared palette (kept in sync with ClinicLocationStatsDrawer for a consistent Stats look).
export const INSIGHT_COLORS = [
  '#7450AA',
  '#FF5F35',
  '#34EBB7',
  '#418571',
  '#9B59B6',
  '#FFA500',
  '#2D9CDB',
  '#EB5757',
]

// Many top-K keys are slugs (e.g. "nutrition-consultation", "session_focus_sleep"). Prettify for display.
export function prettifyKey(key) {
  if (key === null || key === undefined) return '—'
  const str = String(key).trim()
  if (!str) return '—'
  // Leave human strings (with spaces / capitals) mostly intact; only normalise slug separators.
  if (/[\s]/.test(str) || /[a-z].*[A-Z]/.test(str)) return str
  return str
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

/** Titled surface used for each insight block. */
export function InsightsCard({ title, subtitle, children }) {
  return (
    <Card sx={{ boxShadow: 2, height: '100%', borderRadius: '12px' }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {subtitle}
          </Typography>
        ) : null}
        {children}
      </CardContent>
    </Card>
  )
}

/** A compact summary number tile. */
export function StatTile({ label, value, color = '#7450AA' }) {
  return (
    <Card sx={{ height: '100%', color: 'white', borderRadius: '12px', background: color }}>
      <CardContent sx={{ py: 2 }}>
        <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="h4" fontWeight="bold">
          {value ?? 0}
        </Typography>
      </CardContent>
    </Card>
  )
}

/** A 0–1 rate rendered as a percentage pill with a label. Shows "—" when the value is absent. */
export function RatePill({ label, value }) {
  const hasValue = value !== null && value !== undefined && !Number.isNaN(Number(value))
  const pct = Math.round((Number(value) || 0) * 100)
  return (
    <Box
      sx={{
        border: '1px solid rgba(116, 80, 170, 0.25)',
        borderRadius: '10px',
        px: 2,
        py: 1.5,
        backgroundColor: 'rgba(116, 80, 170, 0.05)',
        minWidth: 120,
        flex: '1 1 140px',
      }}
    >
      <Typography variant="h5" fontWeight={700} sx={{ color: '#7450AA' }}>
        {hasValue ? `${pct}%` : '—'}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  )
}

/** A titled card holding a flex-wrap of rate pills. rates: [{ label, value }] (value 0..1 or null). */
export function RateGroup({ title, subtitle, rates }) {
  return (
    <InsightsCard title={title} subtitle={subtitle}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        {rates.map((r) => (
          <RatePill key={r.label} label={r.label} value={r.value} />
        ))}
      </Box>
    </InsightsCard>
  )
}

/** A compact "average" tile (non-integer metric, e.g. avg medications / consultation). */
export function MetricTile({ label, value, suffix }) {
  return (
    <Box
      sx={{
        border: '1px solid rgba(45, 156, 219, 0.3)',
        borderRadius: '10px',
        px: 2,
        py: 1.5,
        backgroundColor: 'rgba(45, 156, 219, 0.06)',
        minWidth: 140,
        flex: '1 1 160px',
      }}
    >
      <Typography variant="h5" fontWeight={700} sx={{ color: '#2D9CDB' }}>
        {value ?? 0}
        {suffix ? <Typography component="span" variant="body2" sx={{ ml: 0.5 }}>{suffix}</Typography> : null}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  )
}

/**
 * Donut showing the %-split of a [{key,count}] list. When `total` (grand total across ALL items, not
 * just the returned top-K) exceeds the visible sum, an "Others" slice is added so the percentages
 * reflect the true split.
 */
export function DonutChart({ items, total, emptyLabel }) {
  if (!items || items.length === 0) {
    return <SectionEmpty label={emptyLabel} />
  }
  const visibleSum = items.reduce((acc, it) => acc + (Number(it?.count) || 0), 0)
  const data = items.map((it) => ({ name: prettifyKey(it?.key), value: Number(it?.count) || 0 }))
  if (total && total > visibleSum) {
    data.push({ name: 'Others', value: total - visibleSum })
  }
  return (
    <ResponsiveContainer width="100%" height={280}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          innerRadius={55}
          outerRadius={90}
          paddingAngle={1}
          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
          labelLine={false}
        >
          {data.map((entry, index) => (
            <Cell key={`d-${index}`} fill={INSIGHT_COLORS[index % INSIGHT_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  )
}

export function SectionEmpty({ label = 'No data for this period' }) {
  return (
    <Box sx={{ py: 4, textAlign: 'center' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
  )
}

export function InsightsLoading() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
      <CircularProgress />
    </Box>
  )
}

/**
 * Horizontal "top N" bar list for [{ key, count }] items. Each row is labelled with the prettified
 * key and annotated with its count.
 */
export function TopKBarChart({ items, color = '#7450AA', emptyLabel }) {
  if (!items || items.length === 0) {
    return <SectionEmpty label={emptyLabel} />
  }
  const data = items.map((it) => ({ label: prettifyKey(it?.key), count: Number(it?.count) || 0 }))
  const height = Math.max(120, data.length * 40)
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 36, bottom: 4, left: 8 }}>
        <XAxis type="number" hide allowDecimals={false} />
        <YAxis
          type="category"
          dataKey="label"
          width={150}
          tick={{ fontSize: 12, fill: '#475569' }}
          interval={0}
        />
        <Tooltip
          cursor={{ fill: 'rgba(116, 80, 170, 0.06)' }}
          contentStyle={{ borderRadius: 8, border: '1px solid #e0e0e0' }}
        />
        <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={20}>
          {data.map((entry, index) => (
            <Cell key={`c-${index}`} fill={color} />
          ))}
          <LabelList dataKey="count" position="right" style={{ fill: '#334155', fontSize: 12 }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

/** Grid of StatTiles from a [{label, value, color}] spec. */
export function StatTileRow({ tiles }) {
  return (
    <Grid container spacing={2}>
      {tiles.map((t, i) => (
        <Grid item xs={6} sm={3} key={t.label}>
          <StatTile label={t.label} value={t.value} color={t.color || INSIGHT_COLORS[i % INSIGHT_COLORS.length]} />
        </Grid>
      ))}
    </Grid>
  )
}

/**
 * A single metric tile for period comparison: the current value, with its change vs a comparison
 * value (as a %, or an absolute delta when the comparison is zero). Green up / red down.
 */
export function DeltaStat({ label, current, previous, color = '#7450AA' }) {
  const cur = Number(current) || 0
  const prev = Number(previous) || 0
  const diff = cur - prev
  const up = diff > 0
  const down = diff < 0
  const deltaColor = up ? '#2e7d32' : down ? '#c62828' : '#64748b'
  const arrow = up ? '▲' : down ? '▼' : '•'
  const change =
    prev > 0 ? `${diff >= 0 ? '+' : ''}${Math.round((diff / prev) * 100)}%` : diff !== 0 ? `+${diff}` : '—'
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '10px',
        px: 2,
        py: 1.5,
        minWidth: 130,
        flex: '1 1 150px',
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={700} sx={{ color }}>
        {cur}
      </Typography>
      <Stack direction="row" spacing={0.5} alignItems="baseline">
        <Typography variant="caption" sx={{ color: deltaColor, fontWeight: 700 }}>
          {arrow} {change}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          vs {prev}
        </Typography>
      </Stack>
    </Box>
  )
}

/**
 * A line chart of one metric across a chronological list of periods (oldest → newest) — the
 * same-month growth trajectory. `points` is `[{ label, value }]`.
 */
export function TrajectoryChart({ points, color = '#7450AA' }) {
  if (!points || points.length === 0) {
    return <SectionEmpty label="No comparable history" />
  }
  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={points} margin={{ top: 20, right: 24, bottom: 4, left: 4 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
        <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#475569' }} />
        <YAxis allowDecimals={false} width={36} tick={{ fontSize: 12, fill: '#475569' }} />
        <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e0e0e0' }} />
        <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2.5} dot={{ r: 4, fill: color }} activeDot={{ r: 6 }}>
          <LabelList dataKey="value" position="top" style={{ fill: '#334155', fontSize: 12 }} />
        </Line>
      </LineChart>
    </ResponsiveContainer>
  )
}

// ---- Shared time period model + selector (reused across the My Reports sub-tabs) ----
//
// A "period" is a plain object the sub-tabs consume directly:
//   { kind: 'preset'|'month'|'custom', from, to, label, id?, monthAnchor? }
// `from`/`to` are ISO LocalDate strings (the backend takes java.time.LocalDate). `monthAnchor`
// (present only for whole-calendar-month periods, 'YYYY-MM') marks that a "same month, previous
// years" comparison is meaningful for this period.

const ISO = 'YYYY-MM-DD'

export const RANGE_PRESETS = [
  { id: 'last7', label: 'Last 7 days', days: 7 },
  { id: 'last30', label: 'Last 30 days', days: 30 },
  { id: 'last90', label: 'Last 90 days', days: 90 },
]

/** A rolling "last N days" preset period (to = today). */
export function presetPeriod(presetId) {
  const preset = RANGE_PRESETS.find((p) => p.id === presetId) || RANGE_PRESETS[1]
  const to = dayjs()
  return {
    kind: 'preset',
    id: preset.id,
    label: preset.label,
    from: to.subtract(preset.days - 1, 'day').format(ISO),
    to: to.format(ISO),
  }
}

/**
 * A whole-calendar-month period for any day in that month. The end is clamped to today for the
 * current (in-progress) month so we never query into the future.
 */
export function monthPeriod(anchor) {
  const start = dayjs(anchor).startOf('month')
  const monthEnd = start.endOf('month')
  const today = dayjs()
  const end = monthEnd.isAfter(today) ? today : monthEnd
  const isCurrent = start.isSame(today, 'month')
  return {
    kind: 'month',
    monthAnchor: start.format('YYYY-MM'),
    label: isCurrent ? 'This month' : start.format("MMM 'YY"),
    from: start.format(ISO),
    to: end.format(ISO),
  }
}

/** Recent calendar months as dayjs anchors, current month first. */
export function recentMonths(n = 6) {
  const start = dayjs().startOf('month')
  return Array.from({ length: n }, (_, i) => start.subtract(i, 'month'))
}

/** An arbitrary custom range from two dayjs-ish bounds (swaps if reversed). */
export function customPeriod(from, to) {
  let f = dayjs(from)
  let t = dayjs(to)
  if (t.isBefore(f)) [f, t] = [t, f]
  const sameYear = f.isSame(t, 'year')
  return {
    kind: 'custom',
    label: `${f.format(sameYear ? 'MMM D' : 'MMM D, YYYY')} – ${t.format('MMM D, YYYY')}`,
    from: f.format(ISO),
    to: t.format(ISO),
  }
}

/** Default landing period for the panel. */
export const DEFAULT_PERIOD = presetPeriod('last30')

/**
 * The comparable window immediately preceding `period` (equal length). A whole month maps to the
 * previous calendar month; a rolling/custom range maps to the same number of days ending the day
 * before it starts.
 */
export function previousPeriod(period) {
  if (period?.kind === 'month' && period.monthAnchor) {
    return monthPeriod(dayjs(`${period.monthAnchor}-01`).subtract(1, 'month'))
  }
  const from = dayjs(period.from)
  const to = dayjs(period.to)
  const days = to.diff(from, 'day') + 1
  const prevTo = from.subtract(1, 'day')
  const prevFrom = prevTo.subtract(days - 1, 'day')
  return customPeriod(prevFrom, prevTo)
}

/**
 * The same calendar month in each of the previous `years` years (oldest → newest, EXCLUDING the
 * period itself — callers append the current period). Empty unless `period` is a whole month, since
 * "same month last year" is only meaningful for a month.
 */
export function sameMonthPriorYears(period, years = 4) {
  if (period?.kind !== 'month' || !period.monthAnchor) return []
  const base = dayjs(`${period.monthAnchor}-01`)
  const list = []
  for (let i = years; i >= 1; i -= 1) {
    list.push(monthPeriod(base.subtract(i, 'year')))
  }
  return list
}

export function periodChipSx(selected) {
  return {
    cursor: 'pointer',
    fontWeight: selected ? 600 : 400,
    backgroundColor: selected ? 'rgba(116, 80, 170, 0.15)' : 'background.paper',
    color: selected ? '#7450AA' : 'text.secondary',
    border: '1px solid',
    borderColor: selected ? '#7450AA' : 'divider',
  }
}

/**
 * Period selector: rolling presets + recent-month chips + a custom range (calendar popover).
 * Controlled via `value` (a period object) and `onChange(period)`.
 */
export function ReportTimePeriodSelector({ value, onChange }) {
  const [anchorEl, setAnchorEl] = useState(null)
  const [tempStart, setTempStart] = useState(null)
  const [tempEnd, setTempEnd] = useState(null)

  const months = recentMonths(6)
  const isCustom = value?.kind === 'custom'

  const openCustom = (e) => {
    setTempStart(value?.from ? dayjs(value.from) : null)
    setTempEnd(value?.to ? dayjs(value.to) : null)
    setAnchorEl(e.currentTarget)
  }
  const applyCustom = () => {
    if (tempStart && tempEnd) onChange(customPeriod(tempStart, tempEnd))
    setAnchorEl(null)
  }

  return (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, mr: 0.5 }}>
        Period
      </Typography>

      {RANGE_PRESETS.map((preset) => {
        const selected = value?.kind === 'preset' && value.id === preset.id
        return (
          <Chip
            key={preset.id}
            label={preset.label}
            size="small"
            onClick={() => onChange(presetPeriod(preset.id))}
            sx={periodChipSx(selected)}
          />
        )
      })}

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

      {months.map((m) => {
        const anchor = m.format('YYYY-MM')
        const selected = value?.kind === 'month' && value.monthAnchor === anchor
        return (
          <Chip
            key={anchor}
            label={m.isSame(dayjs(), 'month') ? 'This month' : m.format("MMM 'YY")}
            size="small"
            onClick={() => onChange(monthPeriod(m))}
            sx={periodChipSx(selected)}
          />
        )
      })}

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

      <Chip
        icon={<CalendarMonthIcon sx={{ fontSize: 16 }} />}
        label={isCustom ? value.label : 'Custom'}
        size="small"
        onClick={openCustom}
        sx={periodChipSx(isCustom)}
      />

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: { xs: 300, sm: 340 } }}>
          <DateRangeCalendarPicker
            startDate={tempStart}
            endDate={tempEnd}
            onStartDateChange={setTempStart}
            onEndDateChange={setTempEnd}
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
            <Button size="small" onClick={() => setAnchorEl(null)} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={!tempStart || !tempEnd}
              onClick={applyCustom}
              sx={{
                textTransform: 'none',
                backgroundColor: '#7450AA',
                '&:hover': { backgroundColor: '#5e3f8a' },
              }}
            >
              Apply
            </Button>
          </Stack>
        </Box>
      </Popover>
    </Stack>
  )
}
